import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { Blade, IBladeComponent } from 'src/app/modules/blades/models/blade-component';
import { Vehicle, VehicleActivity, VehicleActivityTypeHelper } from 'src/app/modules/shared/models';
import { VehicleInfoService } from 'src/app/modules/core/services/vehicle-info.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { AppStateService } from '@modules/core/services/app-state.service';
import { TrafficDeviationTripService } from '@components/traffic-deviations/services/traffic-deviation-trip.service';
import { APP_CONTEXT } from '@modules/core/models/app-state';
import { VehicleTripInfoHelper } from '@components/vehicle/vehicle-trip-info/vehicle-trip-info.helper';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { VehicleStore } from '@modules/core/store';
import { TrafficDeviationAccessService } from '@modules/core/services/traffic-deviation-access.service';
import { isEmptyError } from '@modules/core/services/service.helpers';
import { LoggingHelperService } from '@modules/shared/helpers/logging-helper.service';

import { LoggingService } from '@modules/shared/services/logging.service';
import { VehicleActivityType } from '@modules/shared/models/vehicle/vehicle-activity-type';

@Component({
    selector: 'app-vehicle-activity',
    templateUrl: './vehicle-activity.component.html',
    styleUrls: ['./vehicle-activity.component.scss'],
    imports: [BladeHeaderComponent, TranslateModule, MatDividerModule, MatButtonModule, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.Eager
})
@Blade({
    name: 'VehicleActivityComponent'
})
export class VehicleActivityComponent implements OnInit, OnDestroy, IBladeComponent {
    private readonly _route = inject(ActivatedRoute);
    private readonly _vehicleInfoService = inject(VehicleInfoService);
    private readonly _appState = inject(AppStateService);
    private readonly _trafficDeviationService = inject(TrafficDeviationTripService);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _router = inject(Router);
    private readonly _trafficDeviationAccessService = inject(TrafficDeviationAccessService);
    private readonly _loggingService = inject(LoggingService);
    private readonly _loggingHelperService = inject(LoggingHelperService);

    bladeIndex: number;
    public title$: Observable<string|undefined>;

    public vehicleActivity: VehicleActivity;
    public routeNumber: string;
    public tripNumber: string;
    public time: string;
    public activityType: string;
    public activityTypeColor: string;

    public isLoading$: Observable<boolean>;
    public isOpenTrafficDeviationVisible$: Observable<boolean>;

    private _vehicleActivityId: string;
    private readonly _title$ = new BehaviorSubject(undefined);
    private readonly _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    private readonly _isOpenTrafficDeviationVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this.isLoading$ = this._isLoading$.asObservable();
        this.isOpenTrafficDeviationVisible$ = this._isOpenTrafficDeviationVisible$.asObservable();
        this.title$ = this._title$.asObservable();
    }

    canDeactivate(): boolean {
        return true;
    }

    ngOnInit(): void {
        this._route.params
        .pipe(takeUntil(this._ngUnsubscribe$))
        .subscribe(params => {
            this._vehicleActivityId = params['vehicleActivityId'];
            this._isLoading$.next(true);
            this._setVehicleActivity(undefined); // reset
            this._handleVehicleActivity();
        });

        this._loggingHelperService.logPageViewCount('VehicleActivity', this._router.url);
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public closeBlade() {
        const outlet = this._route.outlet;
        this._router.navigate(['', { outlets: { [outlet]: null } }]);
    }

    public async openTrafficDeviation() {
        const { items: activityPointListItem } = VehicleTripInfoHelper.getActivityPointItems(false, this.vehicleActivity, null, null, this._appState);

        let vehicle: Vehicle;
        if (this.vehicleActivity?.inventoryNumber) {
            vehicle = this._vehicleStore.stateSnapshot.vehicles?.get(this.vehicleActivity.inventoryNumber);
        }

        if (!vehicle) return; 

        this._trafficDeviationService.addVehicleActivity(new Vehicle({
            ...vehicle, 
            routeNumber: this.vehicleActivity.routePublicName, 
            tripNumber: this.vehicleActivity.tripNumber, 
            trafficDate: this.vehicleActivity.trafficDate, 
        }), activityPointListItem);

        let currentContext;
        try {
            currentContext = await firstValueFrom(this._appState.getPropertyState('selectedContext'));
        } catch (error) {
            if (isEmptyError(error)) {
                currentContext = undefined;
            } else {
                this._loggingService.logException(error);
                console.error('Failed to get current context:', error);
                currentContext = undefined;
            }
        }

        if (currentContext !== APP_CONTEXT.NEW_TRAFFIC_DEVIATION) {
            this._appState.changeContext(APP_CONTEXT.NEW_TRAFFIC_DEVIATION);
        }

        let currentScope;
        try {
            currentScope = await firstValueFrom(this._trafficDeviationsStore.deviationScope$);
        } catch (error) {
            if (isEmptyError(error)) {
                currentScope = undefined;
            } else {
                this._loggingService.logException(error);
                console.error('Failed to get current scope:', error);
                currentScope = undefined;
            }
        }

        if (currentScope !== 'COMING_TRIP') {
            const selectedDeviationType = 'DeviationInformation';
            this._trafficDeviationsStore.updateTrafficDeviationState({selectedDeviationType, deviationScope: 'COMING_TRIP'});
        }
    }

    private async _handleVehicleActivity() {
        const va = await this._getVehicleActivity(this._vehicleActivityId);
        this._setVehicleActivity(va);
        this._isLoading$.next(false);
        await this._setIsOpenTrafficDeviationVisible();
    }

    private async _getVehicleActivity(vehicleActivityId: string): Promise<VehicleActivity> {
        try {
            return await firstValueFrom(this._vehicleInfoService.getVehicleActivityById(
                vehicleActivityId
            ));
        } catch (error) {
            if (isEmptyError(error)) {
                return undefined;
            }

            this._loggingService.logException(error);
            console.error('Failed to get vehicle activity by id:', error);

            return undefined;
        }
    }

    private _setVehicleActivity(va: VehicleActivity) {
        this.vehicleActivity = va;
        this.routeNumber = va?.routeName;
        this.tripNumber = va?.tripNumber;
        this.time = va ? `${va.startTime} - ${va.endTime}` : '';
        this.activityTypeColor = VehicleActivityTypeHelper.getColor(va?.type);
        this.activityType = VehicleActivityTypeHelper.getString(va?.type);
        this._title$.next(va?.tripDescription);
        this._isOpenTrafficDeviationVisible$.next(false);
    }

    private async _setIsOpenTrafficDeviationVisible() {
        if (!this.vehicleActivity?.inventoryNumber) {
            return;
        }

        const vehicle = this._vehicleStore.stateSnapshot.vehicles?.get(this.vehicleActivity.inventoryNumber);
        const isVisible = this.vehicleActivity.type === VehicleActivityType.TRIPTRIP
            && (await this._trafficDeviationAccessService.isTrafficDeviationEnabled(vehicle?.garageCode));

        this._isOpenTrafficDeviationVisible$.next(isVisible);
    }
}